/**
 * Dialog.tsx — Rivmoon Console 共享弹窗壳
 *
 * 移植自原型 design/Novel-admin/src/dialogs.jsx (DialogShell) + dialogs2.jsx (DialogShell/Drawer/ConfirmDialog)。
 * 导出三个组件，页面统一复用，禁止各页自造弹窗壳：
 *   - Dialog        居中模态：backdrop + blur（.dialog-backdrop/.dialog），ESC 关闭、点 backdrop 关闭、内容 stopPropagation。
 *   - Drawer        右侧抽屉（.drawer），ESC 关闭、点 backdrop 关闭。
 *   - ConfirmDialog 二次确认：危险操作红色 confirm，可选 requireTyping 键入校验、⌘+Enter 确认。
 * 全部经 createPortal 挂到 document.body；JSX/className/inline-style 与原型壳 1:1。
 */

import { createElement, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { KeyboardEvent as ReactKeyboardEvent, ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Icons } from '~/lib/icons';
import { FocusScopeProvider, createFocusScope, useFocusScope } from './focus-scope';

function createModalPortal(content: ReactNode, focusScope: ReturnType<typeof createFocusScope>) {
  return createPortal(
    createElement(FocusScopeProvider, { value: focusScope }, content),
    document.body,
  );
}

function resolveModalReturnFocusTarget(activeElement: Element | null): HTMLElement | null {
  if (!(activeElement instanceof HTMLElement)) return null;
  const menuElement = activeElement.closest<HTMLElement>('[role="menu"]');
  if (!menuElement?.id) return activeElement;
  return (
    Array.from(document.querySelectorAll<HTMLElement>('[aria-controls]')).find((element) =>
      element.getAttribute('aria-controls')?.split(/\s+/).includes(menuElement.id),
    ) ?? activeElement
  );
}

function useModalFocus() {
  const parentFocusScope = useFocusScope();
  const modalShellRef = useRef<HTMLDivElement>(null);
  const modalTitleId = useId();
  const focusScope = useMemo(
    () => createFocusScope(modalShellRef, parentFocusScope),
    [parentFocusScope],
  );
  const returnFocusTargetRef = useRef(resolveModalReturnFocusTarget(document.activeElement));

  useLayoutEffect(() => {
    if (!parentFocusScope) return;
    const activeElement = resolveModalReturnFocusTarget(document.activeElement);
    if (
      activeElement &&
      parentFocusScope.contains(activeElement) &&
      !focusScope.contains(activeElement)
    ) {
      returnFocusTargetRef.current = activeElement;
    } else {
      const parentFocusRestorationFallback =
        parentFocusScope.getFocusRestorationFallbackElement(focusScope);
      if (parentFocusRestorationFallback) {
        returnFocusTargetRef.current = parentFocusRestorationFallback;
      }
    }
    return parentFocusScope.registerNestedFocusScope(focusScope);
  }, [focusScope, parentFocusScope]);

  useEffect(() => {
    const modalShell = modalShellRef.current;
    if (!modalShell) return;
    const parentOwnedActiveElement = resolveModalReturnFocusTarget(document.activeElement);
    if (
      parentFocusScope &&
      parentOwnedActiveElement &&
      parentFocusScope.contains(parentOwnedActiveElement) &&
      !focusScope.contains(parentOwnedActiveElement)
    ) {
      returnFocusTargetRef.current = parentOwnedActiveElement;
    } else if (parentFocusScope && !parentFocusScope.contains(returnFocusTargetRef.current)) {
      const parentFocusRestorationFallback =
        parentFocusScope.getFocusRestorationFallbackElement(focusScope);
      if (parentFocusRestorationFallback) {
        returnFocusTargetRef.current = parentFocusRestorationFallback;
      }
    }
    const activeElement = document.activeElement;
    if (
      focusScope.isTopmostFocusScope() &&
      (!(activeElement instanceof HTMLElement) || !focusScope.contains(activeElement))
    ) {
      modalShell.focus();
    }

    return () => {
      if (modalShell.isConnected) return;
      const closingActiveElement = document.activeElement;
      const focusBelongsToClosingModal =
        closingActiveElement instanceof Node && focusScope.contains(closingActiveElement);
      const focusIsUnclaimed =
        !(closingActiveElement instanceof HTMLElement) ||
        closingActiveElement === document.body ||
        closingActiveElement === document.documentElement ||
        !closingActiveElement.isConnected;
      const returnFocusTarget = returnFocusTargetRef.current;
      if ((focusBelongsToClosingModal || focusIsUnclaimed) && returnFocusTarget?.isConnected) {
        returnFocusTarget.focus();
      }
    };
  }, [focusScope, parentFocusScope]);

  const trapModalFocus = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Tab') return;
    if (!focusScope.isTopmostFocusScope()) return;
    event.preventDefault();
    event.stopPropagation();
    const modalShell = modalShellRef.current;
    if (!modalShell) return;
    const tabbableElements = focusScope.collectTabbableElements();
    if (tabbableElements.length === 0) {
      modalShell.focus();
      return;
    }
    const activeElementIndex = tabbableElements.indexOf(document.activeElement as HTMLElement);
    const focusDirection = event.shiftKey ? -1 : 1;
    const nextTabbableIndex =
      activeElementIndex < 0
        ? event.shiftKey
          ? tabbableElements.length - 1
          : 0
        : (activeElementIndex + focusDirection + tabbableElements.length) % tabbableElements.length;
    tabbableElements[nextTabbableIndex]?.focus();
  };

  return { focusScope, modalShellRef, modalTitleId, trapModalFocus };
}

/** 居中模态弹窗壳 */
export function Dialog({
  title,
  subtitle,
  icon,
  onClose,
  footer,
  header,
  width = 680,
  children,
  closeOnEsc = true,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  icon?: keyof typeof Icons;
  onClose: () => void;
  footer?: ReactNode;
  /** 标题栏与正文之间的固定条（同原型 DialogShell `header` 槽，如步骤条），不随正文滚动 */
  header?: ReactNode;
  width?: number;
  children?: ReactNode;
  // 嵌套 ConfirmDialog 打开时传 false：避免 Esc 同时穿透到本壳的全局监听，
  // 把「取消二次确认」误成「关闭整个弹窗 + 丢弃本地编辑」（codex #14 §2）
  closeOnEsc?: boolean;
}) {
  const { focusScope, modalShellRef, modalTitleId, trapModalFocus } = useModalFocus();
  // ESC 关闭：全局监听，卸载时移除；closeOnEsc=false 时彻底不挂监听（让 Esc 只命中上层嵌套弹窗）
  useEffect(() => {
    if (!closeOnEsc) return;
    const h = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose, closeOnEsc]);

  return createModalPortal(
    <div className="dialog-backdrop" onClick={onClose}>
      <div
        ref={modalShellRef}
        className="dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby={modalTitleId}
        tabIndex={-1}
        style={{
          width,
          maxWidth: '92vw',
          display: 'flex',
          flexDirection: 'column',
          maxHeight: '92vh',
        }}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={trapModalFocus}
      >
        <div
          style={{
            padding: '14px 18px',
            borderBottom: '1px solid var(--border)',
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            flexShrink: 0,
          }}
        >
          {icon && (
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: 7,
                background: 'var(--accent-soft)',
                color: 'var(--accent)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              {createElement(Icons[icon], { size: 16 })}
            </div>
          )}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div id={modalTitleId} style={{ fontWeight: 500, fontSize: 14 }}>
              {title}
            </div>
            {subtitle && (
              <div style={{ fontSize: 11.5, color: 'var(--fg-muted)', marginTop: 2 }}>
                {subtitle}
              </div>
            )}
          </div>
          <button aria-label="关闭" className="btn btn-ghost btn-icon" onClick={onClose}>
            <Icons.IconX size={14} />
          </button>
        </div>
        {header}
        <div style={{ flex: 1, overflow: 'auto', padding: 0 }}>{children}</div>
        {footer && (
          <div
            style={{
              padding: '12px 18px',
              borderTop: '1px solid var(--border)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: 8,
              flexShrink: 0,
            }}
          >
            {footer}
          </div>
        )}
      </div>
    </div>,
    focusScope,
  );
}

/**
 * 弹窗正文留白：`Dialog` 壳的正文区 `padding: 0`（让表格 / 图片这类整幅内容能贴边），
 * 表单 / 说明类正文必须包在本组件里，否则内容紧贴弹窗左右边缘。`gap` 给子项之间的纵向间距。
 */
export function DialogBody({ children, gap }: { children: ReactNode; gap?: number }) {
  return (
    <div
      style={{
        padding: 18,
        ...(gap === undefined ? null : { display: 'flex', flexDirection: 'column', gap }),
      }}
    >
      {children}
    </div>
  );
}

/** 右侧抽屉壳 */
export function Drawer({
  title,
  subtitle,
  icon,
  onClose,
  footer,
  width = 720,
  children,
  closeOnEsc = true,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  icon?: keyof typeof Icons;
  onClose: () => void;
  footer?: ReactNode;
  width?: number;
  children?: ReactNode;
  // 同 Dialog：抽屉内再开 ConfirmDialog 时传 false，Esc 只关上层确认、不连带关掉抽屉丢编辑
  closeOnEsc?: boolean;
}) {
  const { focusScope, modalShellRef, modalTitleId, trapModalFocus } = useModalFocus();
  // ESC 关闭；closeOnEsc=false 时不挂监听
  useEffect(() => {
    if (!closeOnEsc) return;
    const h = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose, closeOnEsc]);

  return createModalPortal(
    <>
      <div
        className="dialog-backdrop"
        onClick={onClose}
        style={{ background: 'oklch(0.1 0.005 250 / 0.3)' }}
      />
      <div
        ref={modalShellRef}
        className="drawer"
        role="dialog"
        aria-modal="true"
        aria-labelledby={modalTitleId}
        tabIndex={-1}
        style={{ width, maxWidth: '92vw' }}
        onKeyDown={trapModalFocus}
      >
        <div
          style={{
            padding: '14px 18px',
            borderBottom: '1px solid var(--border)',
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            flexShrink: 0,
          }}
        >
          {icon && (
            <div
              style={{
                width: 34,
                height: 34,
                borderRadius: 8,
                background: 'var(--accent-soft)',
                color: 'var(--accent)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              {createElement(Icons[icon], { size: 16 })}
            </div>
          )}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div id={modalTitleId} style={{ fontWeight: 500, fontSize: 14 }}>
              {title}
            </div>
            {subtitle && (
              <div style={{ fontSize: 11.5, color: 'var(--fg-muted)', marginTop: 2 }}>
                {subtitle}
              </div>
            )}
          </div>
          <button aria-label="关闭" className="btn btn-ghost btn-icon" onClick={onClose}>
            <Icons.IconX size={14} />
          </button>
        </div>
        <div style={{ flex: 1, overflow: 'auto' }}>{children}</div>
        {footer && (
          <div
            style={{
              padding: '12px 18px',
              borderTop: '1px solid var(--border)',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              flexShrink: 0,
            }}
          >
            {footer}
          </div>
        )}
      </div>
    </>,
    focusScope,
  );
}

/** 默认/警告/危险三态二次确认弹窗；requireTyping 时需键入指定串才能确认 */
export function ConfirmDialog({
  title,
  message,
  details,
  tone = 'default',
  icon,
  confirmLabel = '确认',
  cancelLabel = '取消',
  secondaryLabel,
  requireTyping,
  onConfirm,
  onSecondary,
  onClose,
}: {
  title: ReactNode;
  message: ReactNode;
  // 补充要点列表（message 下方 <ul> 逐条渲染），如「渠道 · Stripe」「placement · pw_xxx」
  details?: string[];
  tone?: 'danger' | 'warn' | 'default';
  // 可选自定义图标：传了则覆盖 footer 确认按钮按 tone 派生的默认图标
  icon?: keyof typeof Icons;
  confirmLabel?: string;
  cancelLabel?: string;
  // 第二动作按钮：传了才渲染（cancel 与 confirm 之间），点击 → onSecondary() 后 onClose()。
  // 用于「双动作确认」（如 [一并上架页面 / 仅启用渠道]）；ReactNode 支持 warn 色 span 等强调文案。
  secondaryLabel?: ReactNode;
  requireTyping?: string;
  onConfirm: () => void;
  onSecondary?: () => void;
  onClose: () => void;
}) {
  const { focusScope, modalShellRef, modalTitleId, trapModalFocus } = useModalFocus();
  const [typed, setTyped] = useState('');
  const canConfirm = !requireTyping || typed === requireTyping;
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'Enter' && canConfirm && (e.metaKey || e.ctrlKey)) {
        onConfirm?.();
        onClose();
      }
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [canConfirm, onClose, onConfirm]);

  // tone → 配色 / 文案 / 图标；传入 icon 时对齐原型，header 与 footer 都使用自定义图标
  const TONE = {
    default: {
      color: 'var(--accent)',
      bg: 'var(--accent-soft)',
      icon: 'IconInfo' as const,
      ring: 'oklch(0.68 0.17 var(--accent-h) / 0.12)',
      label: '确认操作',
    },
    warn: {
      color: 'var(--warn)',
      bg: 'var(--warn-soft)',
      icon: 'IconAlert' as const,
      ring: 'oklch(0.74 0.15 75 / 0.12)',
      label: '需要确认',
    },
    danger: {
      color: 'var(--neg)',
      bg: 'var(--neg-soft)',
      icon: 'IconAlert' as const,
      ring: 'oklch(0.58 0.2 25 / 0.12)',
      label: '危险操作',
    },
  }[tone];
  const IconC = Icons[icon || TONE.icon];

  const confirmBtnStyle =
    tone === 'danger'
      ? {
          background: canConfirm ? 'var(--neg)' : 'var(--bg-muted)',
          borderColor: canConfirm ? 'var(--neg)' : 'var(--border)',
          color: canConfirm ? 'white' : 'var(--fg-muted)',
        }
      : tone === 'warn'
        ? {
            background: canConfirm ? 'var(--warn)' : 'var(--bg-muted)',
            borderColor: canConfirm ? 'var(--warn)' : 'var(--border)',
            color: canConfirm ? 'white' : 'var(--fg-muted)',
          }
        : {
            background: canConfirm ? 'var(--accent)' : 'var(--bg-muted)',
            borderColor: canConfirm ? 'var(--accent)' : 'var(--border)',
            color: canConfirm ? 'white' : 'var(--fg-muted)',
          };

  return createModalPortal(
    <div className="dialog-backdrop" onClick={onClose}>
      <div
        ref={modalShellRef}
        className="dialog"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={modalTitleId}
        tabIndex={-1}
        style={{ width: 460, maxWidth: '92vw', overflow: 'hidden' }}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={trapModalFocus}
      >
        {/* Hero header with subtle tinted background */}
        <div
          style={{
            padding: '20px 22px 18px',
            background: `linear-gradient(180deg, ${TONE.bg} 0%, transparent 100%)`,
            borderBottom: '1px solid var(--border)',
            position: 'relative',
          }}
        >
          <button
            aria-label="关闭"
            className="btn btn-ghost btn-icon"
            onClick={onClose}
            style={{ position: 'absolute', top: 10, right: 10, width: 26, height: 26 }}
          >
            <Icons.IconX size={13} />
          </button>
          <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: 999,
                background: 'var(--bg-elev)',
                boxShadow: `0 0 0 4px ${TONE.ring}, 0 1px 2px oklch(0.18 0.005 80 / 0.06)`,
                color: TONE.color,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <IconC size={18} />
            </div>
            <div style={{ flex: 1, minWidth: 0, paddingTop: 1 }}>
              <div
                style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: 10,
                  color: TONE.color,
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  fontWeight: 600,
                  marginBottom: 4,
                }}
              >
                {TONE.label}
              </div>
              <div
                id={modalTitleId}
                style={{
                  fontFamily: 'var(--font-display)',
                  fontWeight: 600,
                  fontSize: 16,
                  color: 'var(--fg-strong)',
                  letterSpacing: '-0.01em',
                  lineHeight: 1.3,
                  paddingRight: 24,
                }}
              >
                {title}
              </div>
            </div>
          </div>
        </div>

        {/* Body */}
        <div style={{ padding: '16px 22px 18px' }}>
          <div style={{ fontSize: 13, color: 'var(--fg-muted)', lineHeight: 1.6 }}>{message}</div>
          {details && details.length > 0 && (
            <ul
              style={{
                margin: '10px 0 0',
                paddingLeft: 18,
                fontSize: 12,
                color: 'var(--fg-muted)',
                lineHeight: 1.7,
              }}
            >
              {details.map((d) => (
                <li key={d}>{d}</li>
              ))}
            </ul>
          )}
          {requireTyping && (
            <div
              style={{
                marginTop: 14,
                padding: 12,
                background: 'var(--bg-subtle)',
                border: '1px solid var(--border)',
                borderRadius: 6,
              }}
            >
              <div style={{ fontSize: 11.5, color: 'var(--fg-muted)', marginBottom: 6 }}>
                请输入{' '}
                <span
                  className="mono"
                  style={{
                    color: TONE.color,
                    fontWeight: 600,
                    padding: '1px 5px',
                    background: 'var(--bg-elev)',
                    borderRadius: 3,
                    border: '1px solid var(--border)',
                  }}
                >
                  {requireTyping}
                </span>{' '}
                以确认
              </div>
              <input
                className="input mono"
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                placeholder={requireTyping}
                autoFocus
              />
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          style={{
            padding: '12px 18px',
            borderTop: '1px solid var(--border)',
            background: 'var(--bg-subtle)',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <span
            style={{ fontSize: 10.5, color: 'var(--fg-faint)', fontFamily: 'var(--font-mono)' }}
          >
            <span className="kbd">Esc</span> 取消
            {!requireTyping && (
              <>
                {' '}
                · <span className="kbd">⌘</span> <span className="kbd">↵</span> 确认
              </>
            )}
          </span>
          <div style={{ flex: 1 }} />
          <button className="btn" onClick={onClose}>
            {cancelLabel}
          </button>
          {/* 第二动作按钮（仅 secondaryLabel 传入时）：普通 btn、不受 requireTyping gate */}
          {secondaryLabel && (
            <button
              className="btn"
              onClick={() => {
                onSecondary?.();
                onClose();
              }}
            >
              {secondaryLabel}
            </button>
          )}
          <button
            disabled={!canConfirm}
            onClick={() => {
              onConfirm?.();
              onClose();
            }}
            className="btn"
            style={{ ...confirmBtnStyle, cursor: canConfirm ? 'pointer' : 'not-allowed' }}
          >
            {/* 确认按钮图标：传了 icon 用自定义，否则按 tone 派生 */}
            {(() => {
              const FooterIcon = icon
                ? Icons[icon]
                : tone === 'danger'
                  ? Icons.IconTrash
                  : tone === 'warn'
                    ? Icons.IconAlert
                    : Icons.IconCheck;
              return <FooterIcon size={11} />;
            })()}
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    focusScope,
  );
}
